import React, { useCallback, useMemo, useState } from 'react';
import { isEmptyArray, isNotEmptyArray } from '@/utils/formatChecker';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination, Thumbs } from 'swiper/core';
import FunwooImage from '@/components/common/FunwooImage';

import 'swiper/swiper.min.css';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/thumbs/thumbs.min.css';
import Class from './gallery.module.scss';

import Flexbox from '@/components/common/Flexbox';
import { ImPlay3 } from 'react-icons/im';
import YouTube from 'react-youtube';
import classNames from 'classnames';

export interface GallerySection {
  key: string;
  text?: string;
  icon?: React.ReactNode;
  images: Array<string>;
}

interface Props {
  imageSections: Array<GallerySection>;
  videos?: Array<string>;
}

SwiperCore.use([Pagination, Navigation, Thumbs]);

const Gallery: React.FC<Props> = ({ imageSections, videos = [], children }) => {
  const [swiper, setSwiper] = useState<SwiperCore>();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore>();

  const thumbs = useMemo(() => ({ swiper: thumbsSwiper }), [thumbsSwiper]);

  const buttonData = useMemo<
    Array<{
      index: number;
      text?: string;
      icon?: React.ReactNode;
      key: string;
    }>
  >(() => {
    const data: Array<{
      index: number;
      text?: string;
      icon?: React.ReactNode;
      key: string;
    }> = [];
    imageSections.forEach((section, index, array) => {
      if (section.key === 'default') return;
      if (isEmptyArray(section.images)) return;
      const sectionIndex = array.slice(0, index).reduce((acc, cur) => acc + cur.images.length, 0);
      data.push({
        index: sectionIndex,
        text: section.text,
        icon: section.icon,
        key: section.key,
      });
    });
    if (isNotEmptyArray(videos)) {
      const videoSectionIndex = imageSections.reduce((acc, cur) => acc + cur.images.length, 0);
      data.push({
        index: videoSectionIndex,
        text: '影片',
        icon: <ImPlay3 />,
        key: 'video',
      });
    }
    return data;
  }, [imageSections, videos]);

  const totalImages = useMemo(() => imageSections.reduce((acc, cur) => acc + cur.images.length, 0), [imageSections]);

  const slidesPerView = useMemo(() => {
    let defaultValue = 8;
    defaultValue -= buttonData.length;
    return Math.min(defaultValue, imageSections?.[0]?.images?.length);
  }, [buttonData, totalImages]);

  const slideTo = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      const index = parseInt(event.currentTarget.dataset.index ?? '0');
      thumbs.swiper?.slideTo(index);
      swiper?.slideTo(index);
    },
    [thumbs.swiper, swiper]
  );

  const onThumbSwipe = useCallback((swiper: SwiperCore) => setThumbsSwiper(swiper), [setThumbsSwiper]);

  const thumbPlaceholderCount = useMemo(() => {
    const buttonCount = buttonData.length;
    const requiredCount = 8;
    const count = requiredCount - slidesPerView - buttonCount;

    return count > 0 ? count : 0;
  }, [imageSections, buttonData, slidesPerView]);

  return (
    <div id={'gallery'} className={classNames(Class.gallery, 'relative w-full')}>
      <Swiper
        id={'main-swiper'}
        navigation
        thumbs={thumbs}
        className={'relative mb-4 sm:mb-px w-full h-auto flex'}
        slidesPerView={1}
        onSwiper={setSwiper}
        pagination={{
          dynamicBullets: true,
        }}
      >
        {imageSections.map((section) => (
          <React.Fragment key={section.key}>
            {section.images.map((image) => (
              <SwiperSlide className={'max-w-full'} key={image}>
                <FunwooImage src={image} alt={image} width={3} height={2} layout={'fill'} className={'w-full h-full'} />
              </SwiperSlide>
            ))}
          </React.Fragment>
        ))}
        {isNotEmptyArray(videos) && (
          <SwiperSlide className={'max-w-full'}>
            {videos.map((video) => (
              <Video key={video} src={video} />
            ))}
          </SwiperSlide>
        )}
      </Swiper>
      <Flexbox align={'center'} justify={'center'} className={'sm:hidden sm:invisible text-sm text-black'}>
        {buttonData.map((button) => (
          <Flexbox
            type={'button'}
            as={'button'}
            align={'center'}
            key={button.key}
            onClick={slideTo}
            data-index={button.index}
            className={'mr-4 last:mr-0 py-1 px-4 h-8 border border-black rounded-full'}
          >
            <span className={'mr-2.5'}>{button.icon}</span>
            {button.text}
          </Flexbox>
        ))}
      </Flexbox>
      <div
        id={'thumb'}
        className={classNames(
          'hidden invisible sm:visible',
          'sm:flex flex-row',
          'w-full',
          'items-center justify-center overflow-hidden'
        )}
      >
        <Swiper
          id={'thumb-swiper'}
          navigation
          watchSlidesVisibility
          watchSlidesProgress
          className={'flex-1 flex-row z-10'}
          slidesPerView={slidesPerView}
          onSwiper={onThumbSwipe}
          spaceBetween={1}
        >
          {imageSections[0].images.map((image) => {
            return (
              <SwiperSlide
                key={image}
                className={'mr-px last:mr-0'}
                style={{
                  width: `calc(100%/8 - 1px)`,
                }}
              >
                <FunwooImage
                  src={image}
                  alt={image}
                  width={1}
                  height={1}
                  objectFit={'cover'}
                  layout={'fill'}
                  className={'w-full h-auto'}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
        {Array.from({ length: thumbPlaceholderCount }).map((_, index) => (
          <div
            key={index}
            style={{
              width: `calc(100%/8 - 1px)`,
            }}
            className={classNames('relative', 'first:ml-0 ml-px', 'flex-shrink-0', 'bg-black bg-opacity-90')}
          >
            <span className={'block pt-[100%]'} />
          </div>
        ))}
        {buttonData.map((button) => (
          <Flexbox
            type={'button'}
            as={'button'}
            align={'center'}
            justify={'center'}
            key={button.key}
            onClick={slideTo}
            data-index={button.index}
            style={{
              width: `calc(100%/8 - 1px)`,
            }}
            className={classNames(
              'relative',
              'first:ml-0 ml-px',
              'text-sm text-white',
              'flex-shrink-0',
              'bg-black bg-opacity-90'
            )}
          >
            <span className={'block pt-[100%]'} />
            <span className={'absolute'}>{button.text}</span>
          </Flexbox>
        ))}
      </div>
      {children}
    </div>
  );
};

export default Gallery;

const Video: React.FC<{ src: string }> = ({ src }) => {
  const youtudeId = useMemo(() => {
    const url = new URL(src);
    return url.searchParams.get('v') ?? '';
  }, [src]);

  return (
    <div className={'relative w-full'}>
      <div className={'w-full pt-[calc(100%*2/3)]'} />
      <div className={'absolute top-0 left-0 w-full h-full bg-gray50'}>
        <YouTube className={'w-full h-full'} videoId={youtudeId} containerClassName={'w-full h-full'} />
      </div>
    </div>
  );
};
